import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articleMap, getPopularArticles } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'article-platform-entry': 'https://readdy.ai/api/search-image?query=A%20modern%20browser%20window%20displaying%20a%20clean%20web%20interface%20with%20warm%20amber%20and%20cream%20color%20scheme%2C%20abstract%20geometric%20background%2C%20soft%20natural%20lighting%2C%20professional%20digital%20aesthetic%2C%20minimalist%20style&width=1200&height=600&seq=detail-art-1&orientation=landscape',
  'article-register-login': 'https://readdy.ai/api/search-image?query=A%20clean%20signup%20form%20interface%20on%20a%20warm%20beige%20background%2C%20abstract%20floating%20shapes%20in%20golden%20tones%2C%20soft%20lighting%2C%20modern%20minimalist%20UI%20design%2C%20professional%20look&width=1200&height=600&seq=detail-art-2&orientation=landscape',
  'article-platform-overview': 'https://readdy.ai/api/search-image?query=Abstract%20digital%20dashboard%20with%20floating%20data%20cards%20and%20charts%2C%20warm%20golden%20and%20cream%20color%20palette%2C%20soft%20gradient%20background%2C%20clean%20minimalist%20tech%20aesthetic&width=1200&height=600&seq=detail-art-3&orientation=landscape',
  'article-quick-start': 'https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20desk%20setup%20with%20an%20open%20notebook%20and%20a%20cup%20of%20coffee%2C%20soft%20cream%20and%20beige%20tones%2C%20gentle%20natural%20lighting%2C%20professional%20and%20inviting%20atmosphere&width=1200&height=600&seq=detail-art-4&orientation=landscape',
  'article-security': 'https://readdy.ai/api/search-image?query=A%20shield%20icon%20as%20an%20abstract%20geometric%20shape%20with%20warm%20golden%20glow%2C%20cream%20background%2C%20soft%20lighting%2C%20clean%20minimalist%20tech%20security%20aesthetic&width=1200&height=600&seq=detail-art-5&orientation=landscape',
  'article-faq-entry': 'https://readdy.ai/api/search-image?query=A%20calm%20help%20center%20scene%20with%20soft%20warm%20lighting%2C%20question%20mark%20shapes%20as%20abstract%20geometric%20decorations%2C%20cream%20and%20beige%20tones%2C%20clean%20minimalist%20style&width=1200&height=600&seq=detail-art-6&orientation=landscape',
  'article-faq-account': 'https://readdy.ai/api/search-image?query=A%20user%20profile%20icon%20as%20an%20abstract%20geometric%20composition%2C%20warm%20golden%20and%20cream%20tones%2C%20soft%20lighting%2C%20clean%20minimalist%20style&width=1200&height=600&seq=detail-art-7&orientation=landscape',
  'article-faq-usage': 'https://readdy.ai/api/search-image?query=Abstract%20geometric%20connected%20nodes%20representing%20network%20and%20functionality%2C%20warm%20amber%20and%20cream%20tones%2C%20soft%20gradient%20background%2C%20clean%20minimalist%20tech%20aesthetic&width=1200&height=600&seq=detail-art-8&orientation=landscape',
  'article-terms': 'https://readdy.ai/api/search-image?query=A%20clean%20document%20icon%20as%20abstract%20geometric%20shapes%2C%20warm%20beige%20and%20cream%20tones%2C%20soft%20lighting%2C%20minimalist%20legal%20aesthetic&width=1200&height=600&seq=detail-art-9&orientation=landscape',
  'article-privacy': 'https://readdy.ai/api/search-image?query=A%20lock%20icon%20as%20abstract%20geometric%20composition%20with%20warm%20golden%20accents%2C%20cream%20background%2C%20soft%20lighting%2C%20clean%20minimalist%20aesthetic&width=1200&height=600&seq=detail-art-10&orientation=landscape',
};

function getCategoryLabel(cat: string): string {
  if (cat === 'guide') return '使用指南';
  if (cat === 'features') return '平台功能';
  if (cat === 'faq') return '常见问题';
  return cat;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getPopularArticles(3).filter((a) => a.slug !== slug).slice(0, 2);
  const canonicalPath = `/article/${slug}`;

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.summary,
          datePublished: article.publishDate,
          author: {
            '@type': 'Organization',
            name: article.author,
          },
          url: `https://www.17c.com${canonicalPath}`,
        }}
      />

      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="pt-24 pb-10 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: getCategoryLabel(article.category), href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>
        </div>

        <article className="w-full px-4 md:px-6 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <Link
                to={`/category/${article.category}`}
                className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 mb-4 hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {getCategoryLabel(article.category)}
              </Link>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读约 {article.readTime}
                </span>
              </div>
            </div>

            <div className="relative h-64 md:h-80 rounded-xl overflow-hidden mb-10">
              <img
                src={articleImages[article.imageSeq] || articleImages['article-platform-entry']}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 text-xs font-medium rounded-full bg-background-100 text-foreground-600 border border-background-200/70"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to={`/category/${article.category}`}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{getCategoryLabel(article.category)}
                </Link>
                <div className="flex items-center gap-2">
                  <Link
                    to="/"
                    className="text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-16 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-full sm:w-36 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={articleImages[ra.imageSeq] || articleImages['article-platform-entry']}
                        alt={ra.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{ra.summary}</p>
                      <span className="text-xs text-foreground-400">{ra.publishDate}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}